import api from '../config/api';
import { AuthResponse, LoginCredentials, RegisterData, User } from '../types';

export const authService = {
  // Register new user
  register: async (data: RegisterData): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  // Verify email with OTP
  verifyEmail: async (email: string, otp: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/verify-email', { email, otp });
    if (response.data.success && response.data.data.token) {
      localStorage.setItem('token', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
    }
    return response.data;
  },

  // Login
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', credentials);
    const data = response.data;
    
    if (data.success && !data.data.requiresMFA) {
      localStorage.setItem('token', data.data.token);
      localStorage.setItem('user', JSON.stringify(data.data.user));
    }
    
    return data;
  },

  // Verify MFA
  verifyMFA: async (userId: string, otp: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/verify-mfa', { userId, otp });
    if (response.data.success) {
      localStorage.setItem('token', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
    }
    return response.data;
  },

  // Forgot password
  forgotPassword: async (email: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  // Reset password
  resetPassword: async (token: string, newPassword: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },

  // Refresh token
  refreshToken: async (refreshToken: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/refresh-token', { refreshToken });
    if (response.data.success) {
      localStorage.setItem('token', response.data.data.token);
    }
    return response.data;
  },

  // Logout
  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  },

  // Get current user from localStorage
  getCurrentUser: (): User | null => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    return !!localStorage.getItem('token');
  },

  // Update phone number
  updatePhone: async (phone: string): Promise<{ success: boolean; message: string; data?: { user: User } }> => {
    const response = await api.put('/auth/phone/update', { phone });
    return response.data;
  },

  // Send phone verification code
  sendPhoneVerification: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/auth/phone/send-verification');
    return response.data;
  },

  // Verify phone with OTP
  verifyPhone: async (otp: string): Promise<{ success: boolean; message: string; data?: { user: User } }> => {
    const response = await api.post('/auth/phone/verify', { otp });
    return response.data;
  },

  // Change password (authenticated)
  changePassword: async (currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/auth/change-password', { currentPassword, newPassword });
    return response.data;
  },
};
